"use strict";
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ObjectResourceStore = void 0;
var ObjectLayerStore_1 = require("./ObjectLayerStore");
var Resource_1 = require("./Resource");
var ResourcePipe_1 = require("./ResourcePipe");
var delay_1 = require("../util/delay");
var noop_1 = require("../util/noop");
var resizeCanvasTo_1 = require("../util/resizeCanvasTo");
/**
 * Loads and stores resources for an object.
 */
var ObjectResourceStore = /** @class */ (function () {
    function ObjectResourceStore(_device, _onResourceChanged, _onResourceError) {
        this._device = _device;
        this._onResourceChanged = _onResourceChanged;
        this._onResourceError = _onResourceError;
        // "Visible resources" = accessible to painters, "hidden resources" = updated versions
        // loading in background while the "old" version remains visible. See .load(...)
        this._visibleResourcesByKey = {};
        this._hiddenResourcesByKey = {};
        this._customCanvasesByKey = {};
        this._changed = false;
        this.layers = new ObjectLayerStore_1.ObjectLayerStore(_device);
    }
    /**
     * Returns a combined promise of all current resources. See {@link PipelineState.promiseAll}.
     */
    ObjectResourceStore.prototype.promiseAll = function () {
        return Promise.all([
            this._promiseOf(this._visibleResourcesByKey),
            this._promiseOf(this._hiddenResourcesByKey)
        ]);
    };
    /**
     * Combines all promises in a resource map into one.
     */
    ObjectResourceStore.prototype._promiseOf = function (resources) {
        return Promise.all(Object.values(resources).map(function (r) { return r.promise; }));
    };
    /**
     * Should be called from {@link Painter.loadResources}. Checks if a resource with the
     * given key + hash has already been loaded, and if not, loads it asynchronously. The
     * resource or its status can be retrieved via {@link require} later.
     *
     * Note that if you're updating an existing resource, {@link require} will still return
     * the old version for a brief duration. This avoids UI flashing for resources with
     * short loading times, such as images from cache.
     *
     * @param key The key identifying the resource.
     * @param load Called to load the resource if needed.
     * @param caching Specifies caching options.
     * @param caching.hash A hash value to compare against a previous call.
     */
    ObjectResourceStore.prototype.load = function (key, load, caching) {
        var resource = this._hiddenResourcesByKey[key] || this._visibleResourcesByKey[key];
        if (!resource || resource.hash !== caching.hash) {
            resource = this._addResource(key, caching.hash, load());
        }
        return new ResourcePipe_1.ResourcePipe(this, resource); // allows for post-processing
    };
    /**
     * Adds or replaces a resource.
     */
    ObjectResourceStore.prototype._addResource = function (key, hash, promise) {
        var _this = this;
        var resource = new Resource_1.Resource(key, hash, promise);
        this._visibleResourcesByKey[key]
            ? this._putHiddenResource(resource)
            : this._putVisibleResource(resource);
        resource.promise
            .then(function () {
            if (!resource.isCancelled()) {
                _this._fireChange();
            }
        })
            .catch(function (e) { return _this._onResourceError("\"" + key + "\" failed to load: " + e); });
        return resource;
    };
    /**
     * Adds a visible resource. If there is a currently visible version of the resource, it will
     * be replaced immediately, even if the new resource is still loading.
     */
    ObjectResourceStore.prototype._putVisibleResource = function (resource) {
        this._visibleResourcesByKey[resource.key] = resource;
    };
    /**
     * Adds an initially hidden resource. May wait for some duration before making the resource
     * visible to painters, to avoid UI flashing (see {@link load}).
     */
    ObjectResourceStore.prototype._putHiddenResource = function (resource) {
        var _this = this;
        this._hiddenResourcesByKey[resource.key] = resource;
        var forceVisibility = Symbol();
        var promises = [
            resource.promise.catch(noop_1.noop),
            delay_1.delay(75, forceVisibility)
        ];
        Promise.race(promises).then(function (result) {
            if (!resource.isCancelled()) {
                _this._makeVisible(resource, result === forceVisibility ? 'forced' : 'loaded');
            }
        });
    };
    /**
     * Replaces a currently visible resource with a newly requested one.
     */
    ObjectResourceStore.prototype._makeVisible = function (newResource, reason) {
        // A newer version of the resource may already have been requested (and possibly even
        // loaded). To avoid bugs due to race conditions, always check if the hidden resource
        // is still newer than the currently visible resource:
        var visibleResource = this._visibleResourcesByKey[newResource.key];
        if (!visibleResource || newResource.isNewerThan(visibleResource)) {
            this._putVisibleResource(newResource);
            if (reason === 'forced') { // resource is still loading, `_fireChange` hasn't been called
                this._fireChange();
            }
        }
        // Similarly, keep in mind the record in `this._hiddenResourcesByKey` may have been
        // replaced in the meantime, don't mindlessly delete `newResource.key`.
        if (this._hiddenResourcesByKey[newResource.key] === newResource) {
            delete this._hiddenResourcesByKey[newResource.key];
        }
    };
    /**
     * Invoke this whenever a visible resource changes.
     */
    ObjectResourceStore.prototype._fireChange = function () {
        this._changed = true;
        this._onResourceChanged();
    };
    /**
     * Returns a resource that was previously requested via {@link load}. Throws if the
     * given key does not match any resources that have been added to this store.
     *
     * @param key The key identifying the resource.
     */
    ObjectResourceStore.prototype.require = function (key) {
        return this._requireVisibleResource(key).forPublic();
    };
    /**
     * Indicates whether a resource for the given key has been requested.
     *
     * @param key The key identifying the resource.
     */
    ObjectResourceStore.prototype.contains = function (key) {
        return !!this._visibleResourcesByKey[key];
    };
    /**
     * Returns the resource with the given key, or throws if there is none.
     */
    ObjectResourceStore.prototype._requireVisibleResource = function (key) {
        var resource = this._visibleResourcesByKey[key];
        if (!resource) {
            throw new Error("No resource requested for key '" + key + "'. Make sure to call "
                + 'ObjectResourceStore.load(...), typically from within Painter.loadResources(...), '
                + 'before attempting to use the resource.');
        }
        return resource;
    };
    /**
     * Gets a custom sized working canvas for use in painters. May be used for purposes such
     * as post-processing (i.e. filtering) images via a {@link ResourcePipe}.
     *
     * @param key The key identifying the canvas.
     * @param size The desired size of the canvas.
     * @param canvasType The desired type of canvas.
     */
    ObjectResourceStore.prototype.canvasFor = function (key, size, canvasType) {
        var canvas = this._customCanvasesByKey[key];
        if (!canvas) {
            canvas = this._device.createCanvas(canvasType);
            this._customCanvasesByKey[key] = canvas;
        }
        resizeCanvasTo_1.resizeCanvasTo(size, canvas);
        return canvas;
    };
    /**
     * Gets the "changed" flag of this store and resets it to {@code false}. The painter
     * uses this to check whether new resources have been loaded since the last time the
     * object was painted.
     */
    ObjectResourceStore.prototype.popChanged = function () {
        var state = this._changed;
        this._changed = false;
        return state;
    };
    /**
     * Discards the resources for the given keys. Added to prevent this bug:
     *
     * Step 1: Add a photo with `frameUrl` X.
     * Step 2: Remove the `frameUrl`, i.e. set it to `null`.
     * Step 3: Set the `frameUrl` to Y.
     *
     * Result: X briefly reappears after step 3 while Y loads, due to the UI flashing
     * prevention built into {@link load}. This is solved by calling {@link discard}
     * between step 2 and 3, in {@link Painter.loadResources}.
     *
     * @param keys The keys of the resources to discard.
     */
    ObjectResourceStore.prototype.discard = function () {
        var e_1, _a;
        var keys = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            keys[_i] = arguments[_i];
        }
        try {
            for (var keys_1 = __values(keys), keys_1_1 = keys_1.next(); !keys_1_1.done; keys_1_1 = keys_1.next()) {
                var key = keys_1_1.value;
                delete this._visibleResourcesByKey[key];
                // Hidden resources need to be cancelled so that the callback in `_putHiddenResource`
                // doesn't doesn't add them to `_visibleResourcesByKey` later when they load.
                if (this._hiddenResourcesByKey[key]) {
                    this._hiddenResourcesByKey[key].cancel();
                    delete this._hiddenResourcesByKey[key];
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (keys_1_1 && !keys_1_1.done && (_a = keys_1.return)) _a.call(keys_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
    };
    /**
     * Deletes and cancels all resources being held by the object.
     *
     * Note: Cancelling the resource prevents {@link _fireChange} from triggering after deletion.
     */
    ObjectResourceStore.prototype.dispose = function () {
        this._disposeAll(this._visibleResourcesByKey);
        this._disposeAll(this._hiddenResourcesByKey);
    };
    ObjectResourceStore.prototype._disposeAll = function (resources) {
        var e_2, _a;
        try {
            for (var _b = __values(Object.keys(resources)), _c = _b.next(); !_c.done; _c = _b.next()) {
                var key = _c.value;
                resources[key].cancel();
                delete resources[key];
            }
        }
        catch (e_2_1) { e_2 = { error: e_2_1 }; }
        finally {
            try {
                if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
            }
            finally { if (e_2) throw e_2.error; }
        }
    };
    return ObjectResourceStore;
}());
exports.ObjectResourceStore = ObjectResourceStore;
//# sourceMappingURL=ObjectResourceStore.js.map